import { useEffect, useMemo, useRef } from "react";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { ArrowLeft, Loader2, Plus, Send, Trash2 } from "lucide-react";
import {
  CollapsibleBlock,
  DocumentHeader,
  DocumentPage,
  DocumentStickyBar,
  useDocumentSaveShortcut,
} from "@/components/shared/document-shell";
import { EntityPicker } from "@/components/shared/entity-picker";
import { ErrorBoundary } from "@/components/shared/error-boundary";
import { Field, FieldGrid, FormSection } from "@/components/shared/form";
import {
  LineGrid,
  LineGridBody,
  LineGridHead,
  LineGridRow,
  LineGridTd,
  LineGridTh,
  blockNumberWheel,
  cellControlClass,
} from "@/components/shared/line-grid";
import { Button } from "@/components/ui/button";
import { Input, NativeSelect } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { formatMoney } from "@/features/clients/constants";
import { useClients } from "@/features/clients/api";
import {
  creditNoteToForm,
  useCreateCreditNote,
  useCreditNote,
  useUpdateCreditNote,
} from "@/features/credit-notes/api";
import { canEditLines } from "@/features/credit-notes/constants";
import {
  emptyCnLine,
  emptyCreditNoteForm,
  creditNoteFormSchema,
  type CreditNoteFormValues,
} from "@/features/credit-notes/schema";
import { useMaterialGrades, useMaterials } from "@/features/materials/api";
import { useIssuedInvoicesForClient } from "@/features/payments/api";
import { computeLineAmounts, computePoTotals } from "@/features/purchase/api";
import { GST_SLABS, listGstSlabs, resolveApplicableTaxes } from "@/features/settings/constants";
import { useCompany, useTaxRates } from "@/features/settings/api";

export function CreditNoteNewPage() {
  return (
    <ErrorBoundary title="Credit note form crashed">
      <CreditNoteFormPage mode="create" />
    </ErrorBoundary>
  );
}

export function CreditNoteEditPage({ cnId }: { cnId: string }) {
  return (
    <ErrorBoundary title="Credit note form crashed">
      <CreditNoteFormPage mode="edit" cnId={cnId} />
    </ErrorBoundary>
  );
}

function CreditNoteFormPage({ mode, cnId }: { mode: "create" | "edit"; cnId?: string }) {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { client?: string; invoice?: string };
  const isEdit = mode === "edit";
  const { data: cn, isLoading } = useCreditNote(isEdit ? cnId : undefined);
  const { data: clients = [] } = useClients();
  const { data: materials = [] } = useMaterials();
  const { data: grades = [] } = useMaterialGrades();
  const { data: company } = useCompany();
  const { data: taxRates = [] } = useTaxRates();
  const createCn = useCreateCreditNote();
  const updateCn = useUpdateCreditNote();
  const hydratedRef = useRef(false);

  const form = useForm<CreditNoteFormValues>({
    resolver: zodResolver(creditNoteFormSchema),
    defaultValues: emptyCreditNoteForm,
    mode: "onBlur",
  });

  const { fields, append, remove } = useFieldArray({ control: form.control, name: "lines" });
  const values = useWatch({ control: form.control });
  const linesEditable = !isEdit || (cn ? canEditLines(cn.status) : true);
  const client = clients.find((c) => c.id === values.client_id);
  const { data: issuedInvoices = [] } = useIssuedInvoicesForClient(values.client_id || undefined);
  const {
    formState: { errors },
  } = form;

  useEffect(() => {
    if (!isEdit || !cn || hydratedRef.current) return;
    form.reset(creditNoteToForm(cn));
    hydratedRef.current = true;
  }, [isEdit, cn, form]);

  useEffect(() => {
    if (isEdit) return;
    if (search.client && !form.getValues("client_id")) {
      form.setValue("client_id", search.client, { shouldDirty: true });
      const c = clients.find((x) => x.id === search.client);
      if (c) {
        form.setValue(
          "bill_to_text",
          [c.name, c.addr1, [c.city, c.pincode].filter(Boolean).join(" — ")].filter(Boolean).join("\n"),
          { shouldDirty: true },
        );
      }
    }
    if (search.invoice && !form.getValues("invoice_id")) {
      form.setValue("invoice_id", search.invoice, { shouldDirty: true });
    }
  }, [isEdit, search.client, search.invoice, clients, form]);

  const clientOptions = useMemo(
    () =>
      clients
        .filter((c) => c.is_active)
        .map((c) => ({ id: c.id, label: `${c.code} · ${c.name}`, keywords: c.name })),
    [clients],
  );

  const materialOptions = useMemo(
    () =>
      materials
        .filter((m) => m.is_active && !m.parent_material_id)
        .map((m) => ({ id: m.id, label: m.name, keywords: m.batch_prefix ?? "" })),
    [materials],
  );

  const slabs = useMemo(() => {
    const fromRates = listGstSlabs(taxRates);
    return fromRates.length ? fromRates : [...GST_SLABS];
  }, [taxRates]);

  const preview = useMemo(() => {
    const lines = values.lines ?? [];
    const clientPos = client?.place_of_supply || client?.state;
    const computed = lines.map((line) =>
      computeLineAmounts(
        {
          qty: Number(line.qty) || 0,
          weight: Number(line.weight) || 0,
          unit_rate: Number(line.unit_rate) || 0,
          gst_slab: Number(line.gst_slab) || 0,
        },
        taxRates,
        company?.state,
        clientPos,
        values.issue_date ?? emptyCreditNoteForm.issue_date,
      ),
    );
    const totals = computePoTotals(
      computed,
      Number(values.discount) || 0,
      Number(values.adjustment) || 0,
    );
    const { mode: gstMode } = resolveApplicableTaxes({
      rates: taxRates,
      gstSlab: 18,
      companyState: company?.state,
      clientPlaceOfSupply: clientPos,
      asOf: values.issue_date,
    });
    return { lines: computed, totals, mode: gstMode };
  }, [values, taxRates, company?.state, client]);

  const saving = createCn.isPending || updateCn.isPending;

  async function save(issue = false) {
    const ok = await form.trigger();
    if (!ok) return;
    const data = form.getValues();
    if (isEdit && cnId) {
      const updated = await updateCn.mutateAsync({
        id: cnId,
        values: data,
        replaceLines: true,
        issue,
      });
      navigate({ to: "/credit-notes/$cnId", params: { cnId: updated.id } });
    } else {
      const created = await createCn.mutateAsync({ values: data, issue });
      navigate({ to: "/credit-notes/$cnId", params: { cnId: created.id } });
    }
  }

  useDocumentSaveShortcut(() => save(false), saving);

  if (isEdit && isLoading) {
    return <p className="py-16 text-center text-sm text-muted-foreground">Loading…</p>;
  }

  if (isEdit && cn && !canEditLines(cn.status)) {
    return (
      <div className="py-16 text-center text-sm">
        <p className="text-muted-foreground">This credit note cannot be edited.</p>
        <Button asChild variant="outline" size="sm" className="mt-3">
          <Link to="/credit-notes/$cnId" params={{ cnId: cn.id }}>
            View
          </Link>
        </Button>
      </div>
    );
  }

  return (
    <DocumentPage>
      <DocumentHeader
        title={isEdit ? `Edit ${cn?.cn_number ?? "credit note"}` : "New credit note"}
        back={
          <Button asChild variant="ghost" size="icon-sm">
            {isEdit && cnId ? (
              <Link to="/credit-notes/$cnId" params={{ cnId }}>
                <ArrowLeft />
              </Link>
            ) : (
              <Link to="/credit-notes">
                <ArrowLeft />
              </Link>
            )}
          </Button>
        }
        actions={
          <>
            <Button type="button" variant="outline" size="sm" disabled={saving} onClick={() => save(false)}>
              {saving ? <Loader2 className="animate-spin" /> : null}
              Save draft
            </Button>
            {linesEditable ? (
              <Button type="button" size="sm" disabled={saving} onClick={() => save(true)}>
                <Send />
                Save & issue
              </Button>
            ) : null}
          </>
        }
      />

      <form
        className="space-y-2"
        onSubmit={(e) => {
          e.preventDefault();
          save(false);
        }}
      >
        <FormSection title="Header" variant="plain">
          <FieldGrid>
            <Field label="Client" required span={4} error={errors.client_id?.message}>
              <EntityPicker
                value={values.client_id ?? ""}
                disabled={!linesEditable && isEdit}
                placeholder="Client…"
                recentKey="clients"
                options={clientOptions}
                onChange={(id) => {
                  form.setValue("client_id", id, { shouldDirty: true, shouldValidate: true });
                  form.setValue("invoice_id", "", { shouldDirty: true });
                  const c = clients.find((x) => x.id === id);
                  if (c) {
                    form.setValue(
                      "bill_to_text",
                      [c.name, c.addr1, [c.city, c.pincode].filter(Boolean).join(" — ")]
                        .filter(Boolean)
                        .join("\n"),
                      { shouldDirty: true },
                    );
                  }
                }}
              />
            </Field>
            <Field label="Issue" required span={2}>
              <Input type="date" {...form.register("issue_date")} />
            </Field>
            <Field label="Against invoice" span={3} hint="Optional">
              <NativeSelect {...form.register("invoice_id")}>
                <option value="">—</option>
                {issuedInvoices.map((inv) => (
                  <option key={inv.id} value={inv.id}>
                    {inv.invoice_number} · {formatMoney(inv.grand_total)}
                  </option>
                ))}
              </NativeSelect>
            </Field>
            <Field label="Reason" span={3}>
              <Input placeholder="Rate revision / return…" {...form.register("reason")} />
            </Field>
          </FieldGrid>
        </FormSection>

        <FormSection
          title="Lines"
          variant="plain"
          aside={
            linesEditable ? (
              <Button type="button" size="xs" variant="outline" onClick={() => append(emptyCnLine())}>
                <Plus />
                Add
              </Button>
            ) : null
          }
        >
          {errors.lines?.message ? (
            <p className="mb-1 text-2xs text-destructive">{errors.lines.message}</p>
          ) : null}
          <LineGrid minWidth="52rem">
            <LineGridHead>
              <LineGridTh className="w-7 text-center">#</LineGridTh>
              <LineGridTh className="min-w-[8rem]">Material</LineGridTh>
              <LineGridTh className="min-w-[4.5rem]">Grade</LineGridTh>
              <LineGridTh className="w-14" align="right">
                Qty
              </LineGridTh>
              <LineGridTh className="w-14" align="right">
                Wt
              </LineGridTh>
              <LineGridTh className="w-[4.5rem]" align="right">
                Rate
              </LineGridTh>
              <LineGridTh className="w-[4.75rem]">GST</LineGridTh>
              <LineGridTh className="w-20" align="right">
                Amt
              </LineGridTh>
              <LineGridTh className="w-7" />
            </LineGridHead>
            <LineGridBody>
              {fields.map((field, index) => {
                const materialId = values.lines?.[index]?.material_id ?? "";
                const gstSlab = Number(values.lines?.[index]?.gst_slab) || 0;
                const { taxes } = resolveApplicableTaxes({
                  rates: taxRates,
                  gstSlab,
                  companyState: company?.state,
                  clientPlaceOfSupply: client?.place_of_supply || client?.state,
                  asOf: values.issue_date,
                });
                const gstLabel =
                  preview.mode === "intra_state" && taxes.length >= 2
                    ? `${gstSlab}% (${taxes.map((t) => t.rate).join("+")})`
                    : `${gstSlab}% (IGST)`;

                return (
                  <LineGridRow key={field.id}>
                    <LineGridTd className="text-center text-muted-foreground">{index + 1}</LineGridTd>
                    <LineGridTd>
                      <EntityPicker
                        dense
                        value={materialId}
                        disabled={!linesEditable}
                        placeholder="Material…"
                        options={materialOptions}
                        onChange={(id) => {
                          form.setValue(`lines.${index}.material_id`, id, { shouldDirty: true });
                          form.setValue(`lines.${index}.material_grade_id`, "", { shouldDirty: true });
                        }}
                      />
                    </LineGridTd>
                    <LineGridTd>
                      <NativeSelect
                        className={cellControlClass()}
                        disabled={!linesEditable}
                        {...form.register(`lines.${index}.material_grade_id`)}
                      >
                        <option value="">—</option>
                        {grades
                          .filter((g) => g.is_active)
                          .map((g) => (
                            <option key={g.id} value={g.id}>
                              {g.name}
                            </option>
                          ))}
                      </NativeSelect>
                    </LineGridTd>
                    <LineGridTd align="right">
                      <Input
                        type="number"
                        step="1"
                        disabled={!linesEditable}
                        className={cellControlClass()}
                        onWheel={blockNumberWheel}
                        {...form.register(`lines.${index}.qty`)}
                      />
                    </LineGridTd>
                    <LineGridTd align="right">
                      <Input
                        type="number"
                        step="0.001"
                        disabled={!linesEditable}
                        className={cellControlClass()}
                        onWheel={blockNumberWheel}
                        {...form.register(`lines.${index}.weight`)}
                      />
                    </LineGridTd>
                    <LineGridTd align="right">
                      <Input
                        type="number"
                        step="0.01"
                        disabled={!linesEditable}
                        className={cellControlClass()}
                        onWheel={blockNumberWheel}
                        {...form.register(`lines.${index}.unit_rate`)}
                      />
                    </LineGridTd>
                    <LineGridTd>
                      <NativeSelect
                        className={cellControlClass()}
                        disabled={!linesEditable}
                        {...form.register(`lines.${index}.gst_slab`)}
                      >
                        {slabs.map((s) => (
                          <option key={s} value={s}>
                            {s === gstSlab ? gstLabel : `${s}%`}
                          </option>
                        ))}
                      </NativeSelect>
                    </LineGridTd>
                    <LineGridTd align="right" className="tabular-nums">
                      {formatMoney(preview.lines[index]?.line_subtotal ?? 0)}
                    </LineGridTd>
                    <LineGridTd>
                      {fields.length > 1 && linesEditable ? (
                        <Button type="button" variant="ghost" size="icon-sm" onClick={() => remove(index)}>
                          <Trash2 />
                        </Button>
                      ) : null}
                    </LineGridTd>
                  </LineGridRow>
                );
              })}
            </LineGridBody>
          </LineGrid>
        </FormSection>

        <CollapsibleBlock title="Notes">
          <Textarea rows={2} className="text-xs" {...form.register("notes")} />
        </CollapsibleBlock>
      </form>

      <DocumentStickyBar>
        <div className="flex flex-wrap items-center gap-3 text-xs">
          <span>
            Tax <strong className="tabular-nums">{formatMoney(preview.totals.tax_total)}</strong>
          </span>
          <span>
            Credit{" "}
            <strong className="tabular-nums text-primary">
              {formatMoney(preview.totals.grand_total)}
            </strong>
          </span>
        </div>
      </DocumentStickyBar>
    </DocumentPage>
  );
}
